import $api from "@/api";
import { ApiPagination } from "@/utils/types";
import { SurveyResponseType } from "@/types/surveys";

export type ChatPetI = {
  id: string;
  name: string;
  image: string | null;
  public_page: boolean;
};

export type ChatReplyPreviewI = {
  id: number;
  username: string;
  content_label: string;
  is_deleted: boolean;
};

export interface ChatMessageI {
  id: number;
  user_id: number | null;
  username: string;
  text: string | null;
  created_at: string;
  edited_at: string | null;
  is_deleted: boolean;
  survey_response: SurveyResponseType | null;
  pet: ChatPetI | null;
  reply_to: ChatReplyPreviewI | null;
  is_own: boolean;
}

export type ChatActivityEvent = {
  id: number;
  user_id: number | null;
  created_at: string;
  is_own: boolean;
};

export type ChatActivityI = {
  online_count: number;
  unread_count: number;
  latest_message_id: number;
  latest_message_created_at: string | null;
  revision: string | null;
  events: ChatActivityEvent[];
};

export async function fetchChatMessages(limit = 100): Promise<ChatMessageI[]> {
  const res = await $api.get<ApiPagination<ChatMessageI>>("/chat/messages/", {
    params: { limit },
  });
  return Array.isArray(res.data?.results) ? res.data.results : [];
}

export async function sendChatMessage(
  text: string,
  replyToId?: number
): Promise<ChatMessageI> {
  const res = await $api.post("/chat/messages/", {
    text,
    reply_to_id: replyToId,
  });
  return res.data;
}

export async function sendSurveyMessage(
  surveyResponseId: number,
  replyToId?: number
): Promise<ChatMessageI> {
  const res = await $api.post("/chat/messages/", {
    survey_response_id: surveyResponseId,
    reply_to_id: replyToId,
  });
  return res.data;
}

export async function sendPetMessage(
  petId: string,
  replyToId?: number
): Promise<ChatMessageI> {
  const res = await $api.post("/chat/messages/", {
    pet_id: petId,
    reply_to_id: replyToId,
  });
  return res.data;
}

export async function fetchChatActivity(
  afterId?: number
): Promise<ChatActivityI> {
  const res = await $api.post("/chat/messages/activity/", {
    after_id: afterId,
  });
  return res.data;
}

export async function markChatRead(
  messageId?: number
): Promise<{ unread_count: number }> {
  const res = await $api.post("/chat/messages/read/", {
    message_id: messageId,
  });
  return res.data;
}

export async function editChatMessage(
  id: number,
  text: string
): Promise<ChatMessageI> {
  const res = await $api.patch(`/chat/messages/${id}/`, { text });
  return res.data;
}

export async function deleteChatMessage(id: number): Promise<void> {
  await $api.delete(`/chat/messages/${id}/`);
}

export async function banUser(id: number): Promise<ChatMessageI> {
  const res = await $api.post("/chat/bans/", { user: id });
  return res.data;
}
